// Small presentational primitives for the SSH Proxy wizard, styled with the
// design-system tokens so the flow reads as one system with the RADIUS / AD
// policy screens (white surfaces, warm ground, mono for machine strings).
import React, { useState } from "react";
import { message } from "antd";
import { Check, Copy } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";

import copyText from "../../../utils/copyText";
export const card = {
  background: AN_COLORS.surface,
  border: `1px solid ${AN_COLORS.line}`,
  borderRadius: 10,
};

// Section heading used at the top of each panel inside a step.
export function PanelHead({ eyebrow, title, desc, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
      <div>
        {eyebrow && (
          <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
            {eyebrow}
          </div>
        )}
        <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 600, color: AN_COLORS.ink, letterSpacing: -0.2 }}>{title}</h3>
        {desc && <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 4, maxWidth: 620, lineHeight: 1.5 }}>{desc}</div>}
      </div>
      {right}
    </div>
  );
}

export function FieldLabel({ children, hint, required }) {
  return (
    <div style={{ fontSize: 11.5, fontWeight: 500, color: AN_COLORS.ink2, marginBottom: 6 }}>
      {children}
      {required && <span style={{ color: AN_COLORS.danger, marginLeft: 3 }}>*</span>}
      {hint && <span style={{ color: AN_COLORS.muted, fontWeight: 400, marginLeft: 6 }}>{hint}</span>}
    </div>
  );
}

// Copy-to-clipboard button that flips to a tick briefly. Used for every command
// and key snippet the admin has to move between machines.
export function CopyButton({ text, label = "Copy", size = "sm" }) {
  const [done, setDone] = useState(false);
  const [messageApi, ctx] = message.useMessage();
  const onCopy = async () => {
    try {
      await copyText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1400);
    } catch {
      messageApi.error("Couldn't copy — select and copy manually");
    }
  };
  const pad = size === "sm" ? "4px 9px" : "6px 12px";
  return (
    <button
      type="button"
      onClick={onCopy}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: pad,
        border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, background: AN_COLORS.surface,
        color: done ? AN_COLORS.success : AN_COLORS.ink2, fontFamily: AN_FONT, fontSize: 12, fontWeight: 500,
        cursor: "pointer", whiteSpace: "nowrap",
      }}
    >
      {ctx}
      {done ? <Check size={13} /> : <Copy size={13} />}
      {done ? "Copied" : label}
    </button>
  );
}

// Monospace command / snippet block with a copy affordance. `multiline` keeps
// idempotent shell snippets readable.
export function CodeBlock({ children, copyText, multiline = false }) {
  return (
    <div style={{ position: "relative", background: "#0B0D12", borderRadius: 8, border: `1px solid ${AN_COLORS.ink2}` }}>
      <pre
        style={{
          margin: 0, padding: "12px 14px", paddingRight: 92, color: "#E6E8EC",
          fontFamily: AN_MONO, fontSize: 12.5, lineHeight: 1.6,
          whiteSpace: multiline ? "pre-wrap" : "pre", overflowX: "auto", wordBreak: multiline ? "break-word" : "normal",
        }}
      >
        {children}
      </pre>
      {copyText != null && (
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          <CopyButton text={copyText} />
        </div>
      )}
    </div>
  );
}

// One inline value the operator reads off (env var, path, fingerprint).
export function InlineValue({ children }) {
  return (
    <code style={{ fontFamily: AN_MONO, fontSize: 12, background: AN_COLORS.tint, color: AN_COLORS.ink2, padding: "1.5px 6px", borderRadius: 5, wordBreak: "break-all" }}>
      {children}
    </code>
  );
}

// Inline notices. Denials are indistinguishable at the client, so the wizard is
// where a misconfiguration has to be made visible — these carry that weight.
const NOTE_TONE = {
  warn: { bg: "var(--an-warnBg)", bd: "var(--an-warnBd)", fg: "var(--an-warnFg)" },
  info: { bg: "var(--an-infoBg)", bd: "var(--an-infoBd)", fg: "var(--an-infoFg)" },
  danger: { bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)", fg: "var(--an-dangerFg)" },
  ok: { bg: "var(--an-successBg)", bd: "var(--an-successBd)", fg: "var(--an-successFg)" },
};
export function Note({ tone = "info", icon, children }) {
  const t = NOTE_TONE[tone] || NOTE_TONE.info;
  return (
    <div style={{ display: "flex", gap: 9, alignItems: "flex-start", background: t.bg, border: `1px solid ${t.bd}`, color: t.fg, borderRadius: 8, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, fontFamily: AN_FONT }}>
      {icon && <span style={{ marginTop: 1, flexShrink: 0 }}>{icon}</span>}
      <div>{children}</div>
    </div>
  );
}

// Small round status dot for the checklist / right rail.
const DOT_TONE = { ok: AN_COLORS.success, warn: AN_COLORS.warn, danger: AN_COLORS.danger, idle: AN_COLORS.line, unknown: AN_COLORS.muted };
export function StatusDot({ tone = "idle" }) {
  return <span style={{ width: 8, height: 8, borderRadius: "50%", background: DOT_TONE[tone] || DOT_TONE.idle, flexShrink: 0, display: "inline-block" }} />;
}
